import { Component, Inject, OnInit, PLATFORM_ID, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { IonContent, Platform, PopoverController } from "@ionic/angular";

import { ImageInterface } from "src/app/types/image";
import { PostCategory, PostItem, RelatedContentInterface } from "src/app/types/post";

import { SeoService } from "src/app/services/seo.service";

import { PopoverSocialShareComponent } from "src/app/components/popover-social-share/popover-social-share.component";
import { SEOSchema } from "src/app/types/seo";
import { environment } from "src/environments/environment";
import { JsonLdService } from "src/app/services/json-ld.service";
import { ApiService } from "src/app/services/api.service";
import { isPlatformBrowser } from "@angular/common";

@Component({
  selector: "app-blog-detail",
  templateUrl: "./blog-detail.page.html",
  styleUrls: ["./blog-detail.page.scss"],
})
export class BlogDetailPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  xhrInProgress: boolean = false;
  featuredImage: ImageInterface;
  data: PostItem;
  relatedContent: PostItem[];
  currentCategory: PostCategory;
  schema: SEOSchema = {
    "@context": "http://schema.org",
    "@type": "BlogPosting",
    url:
      typeof window !== "undefined" ? window.location.href.split("#")[0] : null,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": typeof window !== "undefined" ? window.location.href : "/blog",
    },
    author: {
      "@type": "Organization",
      name:
        environment.environment_name == "clearwater"
          ? environment.environment_title
          : environment.environment_title + " Shopping Centre",
    },
    publisher: {
      "@type": "Organization",
      name: environment.environment_title,
    },
  };
  environmentName: string = environment.environment_title;
  currentDayName: string;
  relatedStores!: PostItem[];
  isBrowser: boolean = false;

  slideOpts = {
    freeMode: false,
    pagination: false,
    breakpoints: {
      280: {
        slidesPerView: 1.2,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2.4,
        spaceBetween: 40,
      },
      1024: {
        slidesPerView: 3.33,
        spaceBetween: 40,
      },
    },
  };

  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private seo: SeoService,
    public popoverController: PopoverController,
    private router: Router,
    private jsonLdService: JsonLdService,
    private API: ApiService,
    @Inject(PLATFORM_ID) platformId: string
  ) {
    this.currentDayName = new Date()
      .toLocaleDateString('en-US', {
        weekday: 'long',
      })
      .toLowerCase();

    this.isBrowser = isPlatformBrowser(platformId);

    this.platform.ready().then(() => {
      this.cordova = this.platform.is("cordova");
    });
  }

  /**
   * Present social share popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: PopoverSocialShareComponent,
      event: ev,
      translucent: true,
      cssClass: "social-popover",
    });
    return await popover.present();
  }

  /**
   * Link to store detail
   */
  linkToDetail(item) {
    this.router.navigateByUrl(`/shop/${item.slug}`);
  }

  /**
   * Find and set store opening to closing time
   */
  storeOpenClose(item) {
    if (item.typeTrading && item.typeTrading[0]) {
      for (let index = 0; index < item.typeTrading.length; index++) {
        const element = item.typeTrading[index];
        if (element.dayName == this.currentDayName) {
          return element.open_text + ' - ' + element.close_text;
          break;
        }
      }
    } else if (item.typeTrading[this.currentDayName]) {
      return (
        item.typeTrading[this.currentDayName].open_text +
        ' - ' +
        item.typeTrading[this.currentDayName].close_text
      );
    }
  }

  ngOnInit() {
    this.route.data.subscribe(
      (response: {
        data: {
          blog?: PostItem;
          competition?: PostItem;
          relatedContent: PostItem[];
        };
      }) => {
        let pageData = response.data.blog;
        if (response.data.competition) pageData = response.data.competition;

        if (pageData.featuredImage) {
          this.featuredImage = {
            id: pageData.featuredImageId,
            ratio: pageData.featuredImageSelectedRatio,
            url: pageData.featuredImage,
            stretched: false,
            withBackground: false,
          };
        }

        if (typeof pageData.category === "string") {
          pageData.category = JSON.parse(pageData.category);
        }

        if (pageData.category?.child?.title) {
          this.currentCategory = pageData.category.child;
        } else {
          this.currentCategory = pageData.category;
        }

        // Set blog data
        this.data = pageData;
        const relatedStores: any = this.data.relatedStores;
        if (response.data.relatedContent)
          this.relatedContent = response.data.relatedContent;

        if (this.isBrowser && relatedStores && relatedStores.length > 0) {
          return this.API.GET(
            `${environment.cdn_url}storeLists/${environment.mall_id}.json`
          ).subscribe({
            next: response => {
              this.relatedStores = response.results.filter(store =>
                relatedStores.includes(store.slug)
              );
            }
          })
        }

        // Set Meta & Social tags
        if (!this.cordova) {
          let schemaData = { ...this.schema };

          if (this.featuredImage?.url) {
            schemaData = {
              ...schemaData,
              image: [this.featuredImage.url],
              publisher: {
                ...schemaData.publisher,
                logo: {
                  "@type": "ImageObject",
                  url: this.featuredImage.url,
                },
              },
            };
          }

          if (this.data.updatedAt) {
            schemaData = {
              ...schemaData,
              datePublished: this.data.updatedAt,
            };
          }

          if (this.data.updatedAt) {
            schemaData = {
              ...schemaData,
              dateModified: this.data.updatedAt,
            };
          }

          // Title tag
          if (this.data.seo?.title) {
            this.seo.setTitle(this.data.seo.title);
            schemaData = {
              ...schemaData,
              headline: this.data.seo.title,
            };
          } else {
            this.seo.setTitle(this.data.title);
            schemaData = {
              ...schemaData,
              headline: this.data.title,
            };
          }

          // Meta tags
          if (this.data.seo) {
            this.seo.setMeta(this.data.seo);
            this.seo.setRobotsMeta(this.data.seo);

            schemaData = {
              ...schemaData,
              description: this.data.seo.description,
            };
          }

          // Social tags
          if (this.data.social) {
            if (!this.data.social.socialImage && this.data.socialImage)
              this.data.social.socialImage = this.data.socialImage;

            this.seo.setSEOMeta(this.data.social);
          }

          // Author
          if (this.data.createdBy) {
            this.seo.setAuthor(this.data.createdBy);
          }

          // Canonical URL
          if (this.data.seo?.canonical_url) {
            this.seo.setCanonicalURL(this.data.seo);
          }

          this.schema = { ...this.schema, ...schemaData };
          this.jsonLdService.insertSchema(schemaData);
        }
      }
    );
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }
}
